<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <KeyRound class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Passkeys
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-6">
            <p class="text-xs leading-4 text-muted-foreground max-w-[411px]">
                Sign in with Touch ID, Face ID, Windows Hello or a security key instead of a password. You can also turn on passkeys under Two Factor Authentication, so signing in with a password asks for a passkey as the second step.
            </p>

            <ul v-if="passkeys.length > 0" class="divide-y divide-border rounded-lg border border-border">
                <li
                    v-for="passkey in passkeys"
                    :key="passkey.id"
                    class="flex items-center justify-between gap-4 p-4"
                >
                    <div class="min-w-0">
                        <p class="truncate text-sm font-medium text-foreground">
                            {{ passkey.name }}
                            <span v-if="passkey.authenticator" class="font-normal text-muted-foreground">
                                · {{ passkey.authenticator }}
                            </span>
                        </p>
                        <p class="text-xs text-muted-foreground">{{ describePasskey(passkey) }}</p>
                    </div>

                    <component
                        :is="confirmComponent"
                        :allow-passkey="passkeys.length > 0"
                        @confirmed="pendingDelete = passkey"
                    >
                        <Button
                            variant="ghost"
                            size="icon"
                            :disabled="deletingId === passkey.id"
                            :aria-label="`Remove ${passkey.name}`"
                        >
                            <Trash2 class="size-4" />
                        </Button>
                    </component>
                </li>
            </ul>

            <p v-if="!isSupported" class="text-sm text-muted-foreground">
                This browser doesn't support passkeys.
            </p>

            <component
                :is="confirmComponent"
                v-else
                :allow-passkey="passkeys.length > 0"
                @confirmed="naming = true"
            >
                <Button variant="outline" :disabled="isLoading">
                    <Plus class="size-4" />
                    Add passkey
                </Button>
            </component>
        </CardContent>
    </Card>

    <Dialog :open="naming" @update:open="(val) => !val && closeNaming()">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Add a passkey</DialogTitle>
                <DialogDescription>
                    Give this passkey a name so you can recognise it later, then follow your browser's prompt.
                </DialogDescription>
            </DialogHeader>

            <div>
                <Label for="passkey-name-input">Name</Label>
                <Input
                    id="passkey-name-input"
                    v-model="name"
                    placeholder="e.g. MacBook Pro"
                    class="mt-2"
                    @keydown.enter="addPasskey"
                />
            </div>

            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="closeNaming">Cancel</Button>
                <Button size="lg" :disabled="!name.trim() || isLoading" @click="addPasskey">Continue</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>

    <Dialog :open="!!pendingDelete" @update:open="(val) => !val && (pendingDelete = null)">
        <DialogContent class="sm:max-w-md">
            <DialogHeader>
                <DialogTitle>Remove passkey</DialogTitle>
                <DialogDescription>
                    You will no longer be able to sign in with <span class="font-medium text-foreground">{{ pendingDelete?.name }}</span>. It stays saved on your device or password manager until you delete it there too.
                </DialogDescription>
            </DialogHeader>
            <DialogFooter class="mt-4">
                <Button size="lg" variant="ghost" @click="pendingDelete = null">Cancel</Button>
                <Button size="lg" variant="destructive" @click="removePasskey">Remove</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { router } from '@inertiajs/vue3';
import { UserCancelledError } from '@laravel/passkeys';
import { usePasskeyRegister } from '@laravel/passkeys/vue';
import { KeyRound, Plus, Trash2 } from 'lucide-vue-next';
import { toast } from 'vue-sonner';
import { route } from 'ziggy-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import IPasskey from '@/types/Settings/IPasskey';
import { describePasskey } from '@/utils/passkeys';
import ConfirmsPassword from './ConfirmsPasswordV2.vue';
import ConfirmsOtp from './ConfirmsOtpV2.vue';

const props = defineProps<{
    passkeys: IPasskey[];
    hasPassword: boolean;
}>();

const confirmComponent = computed(() =>
    props.hasPassword ? ConfirmsPassword : ConfirmsOtp
);

const naming = ref(false);
const name = ref('');
const pendingDelete = ref<IPasskey | null>(null);
const deletingId = ref<number | null>(null);

const { register, isLoading, isSupported } = usePasskeyRegister({
    onSuccess: () => {
        toast.success('Passkey added');
        router.reload({ only: ['passkeys'] });
    },
    onError: (error) => {
        if (error instanceof UserCancelledError) {
            return;
        }

        toast.error(error.message || 'Could not add the passkey.');
    },
});

const closeNaming = () => {
    naming.value = false;
    name.value = '';
};

const addPasskey = async () => {
    const trimmed = name.value.trim();

    if (!trimmed) return;

    closeNaming();
    await register(trimmed);
};

const removePasskey = () => {
    const passkey = pendingDelete.value;

    if (!passkey) return;

    pendingDelete.value = null;
    deletingId.value = passkey.id;

    router.delete(route('passkey.destroy', passkey.id), {
        preserveScroll: true,
        // Removing the last passkey also turns off passkey 2FA, which lives on the user
        only: ['passkeys', 'user'],
        onSuccess: () => toast.success('Passkey removed'),
        onError: (errors) => toast.error((Object.values(errors)[0] as string) || 'Could not remove the passkey.'),
        onFinish: () => {
            deletingId.value = null;
        },
    });
};
</script>
